import React from 'react';
import {
  Sparkles, RefreshCw, Save, Printer, GraduationCap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AssessmentBlueprint } from './AssessmentBlueprint';

export function ExamTab({
  examForm, setExamForm, examLoading, generateTermlyExam,
  generatedExam, saveTermlyExam, printTermlyExam,
  getSubjectOptions, getYearOptions
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-indigo-600" /> Termly Exam</CardTitle>
          <CardDescription>Generate a full exam paper based on topics covered</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Subject</Label>
            <Select value={examForm.subject} onValueChange={(value) => setExamForm({...examForm, subject: value})}>
              <SelectTrigger data-testid="exam-select-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
              <SelectContent>
                {getSubjectOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Year Level</Label>
              <Select value={String(examForm.year_level)} onValueChange={(value) => setExamForm({...examForm, year_level: parseInt(value)})}>
                <SelectTrigger data-testid="exam-select-year"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {getYearOptions().map(year => (
                    <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Term</Label>
              <Select value={String(examForm.term)} onValueChange={(value) => setExamForm({...examForm, term: parseInt(value)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Term 1</SelectItem>
                  <SelectItem value="2">Term 2</SelectItem>
                  <SelectItem value="3">Term 3</SelectItem>
                  <SelectItem value="4">Term 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Duration (min)</Label>
              <Input type="number" value={examForm.duration_minutes} onChange={(e) => setExamForm({...examForm, duration_minutes: parseInt(e.target.value) || 60})} />
            </div>
            <div className="space-y-2">
              <Label>Total Marks</Label>
              <Input type="number" value={examForm.total_marks} onChange={(e) => setExamForm({...examForm, total_marks: parseInt(e.target.value) || 50})} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Topics Covered This Term</Label>
            <textarea
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              placeholder={"List the topics covered, e.g.\n- Introduction to Programming\n- Data Types\n- Variables and Constants\n- Input/Output"}
              value={examForm.topics_covered}
              onChange={(e) => setExamForm({...examForm, topics_covered: e.target.value})}
              data-testid="exam-topics-input"
            />
          </div>

          <Button className="w-full" onClick={generateTermlyExam} disabled={examLoading || !examForm.topics_covered} data-testid="generate-exam-btn">
            {examLoading ? <><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Generating Exam...</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate Exam Paper</>}
          </Button>
        </CardContent>
      </Card>

      <div className="lg:col-span-2">
        <Card className="min-h-[400px]">
          <CardContent className="p-6">
            {examLoading && (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
                <p className="text-muted-foreground">Generating termly exam paper...</p>
              </div>
            )}
            {!examLoading && !generatedExam && (
              <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground">
                <GraduationCap className="h-12 w-12 opacity-30" />
                <p>Enter topics covered and click Generate to create an exam paper</p>
              </div>
            )}
            {generatedExam && (
              <div className="space-y-4">
                <AssessmentBlueprint
                  data={generatedExam}
                  metadata={{
                    subject: getSubjectOptions().find(s => s.value === examForm.subject)?.label || examForm.subject,
                    year_level: examForm.year_level,
                    term: examForm.term,
                    total_marks: examForm.total_marks,
                    duration_minutes: examForm.duration_minutes,
                  }}
                  type="exam"
                />

                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-indigo-800">
                    Term {examForm.term} Exam - Year {examForm.year_level} {getSubjectOptions().find(s => s.value === examForm.subject)?.label || examForm.subject}
                  </h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={printTermlyExam} data-testid="print-exam-btn">
                      <Printer className="mr-2 h-4 w-4" /> Print
                    </Button>
                    <Button size="sm" onClick={saveTermlyExam} data-testid="save-exam-btn">
                      <Save className="mr-2 h-4 w-4" /> Save
                    </Button>
                  </div>
                </div>
                <div className="flex gap-3 text-sm text-muted-foreground">
                  <span>Duration: {examForm.duration_minutes} min</span>
                  <span>Total: {examForm.total_marks} marks</span>
                </div>
                {(generatedExam.sections || []).map((section, idx) => (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    <div className="bg-indigo-800 text-white px-4 py-2 font-semibold text-sm">
                      {section.title} {section.marks ? `(${section.marks} marks)` : ''}
                    </div>
                    {section.instructions && <p className="text-xs text-gray-500 px-4 pt-2">{section.instructions}</p>}
                    <div className="p-4 space-y-3">
                      {(section.questions || []).map((q, qi) => (
                        <div key={qi} className="text-sm border-b last:border-0 pb-2">
                          <div className="flex justify-between">
                            <span><strong>{q.number || qi + 1}.</strong> {q.text || q.question}</span>
                            <span className="text-xs text-gray-400 whitespace-nowrap ml-2">[{q.marks || 1} mk{(q.marks || 1) > 1 ? 's' : ''}]</span>
                          </div>
                          {q.options && (
                            <div className="pl-6 mt-1 space-y-0.5 text-xs text-gray-600">
                              {q.options.map((o, oi) => <div key={oi}>{String.fromCharCode(65 + oi)}. {o}</div>)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
